/**
 * Procedural line-drawn elevations of the four asset types this practice deals
 * in. Drawn, not photographed, on purpose: stock photography of buildings
 * this practice didn't transact would be a quiet lie, and an elevation is the honest
 * way a commercial property gets represented before there's anything to show.
 *
 * All linework is currentColor, so a glyph works on paper or on cyanotype. Line
 * weights follow drawing convention: heavy for cut edges and ground, medium for
 * object edges, hairline for glazing and panel divisions.
 */

import type { ReactElement } from 'react';

export type GlyphType = 'office' | 'retail' | 'industrial' | 'land';

interface Props {
  type: GlyphType;
  className?: string;
}

const GROUND = 92;

function Ground() {
  return <path d={`M4 ${GROUND}H156`} strokeWidth="1.6" />;
}

function Office() {
  return (
    <>
      {/* Parapet cap, slightly proud of the body */}
      <path d="M42 16H118" strokeWidth="1.4" />
      <path d="M45 16V92M115 16V92" strokeWidth="1.4" />
      {/* Floor plates */}
      <path d="M45 29H115M45 42H115M45 55H115M45 68H115M45 81H115" strokeWidth="0.7" />
      {/* Mullions */}
      <path d="M62.5 16V92M80 16V92M97.5 16V92" strokeWidth="0.7" />
      {/* Entrance */}
      <path d="M71 92V80H89V92" strokeWidth="1.4" />
      <Ground />
    </>
  );
}

function Retail() {
  return (
    <>
      {/* Eave, overhanging the body */}
      <path d="M16 42H144" strokeWidth="1.4" />
      <path d="M20 42V92M140 42V92" strokeWidth="1.4" />
      {/* Signage band */}
      <path d="M20 54H140" strokeWidth="0.7" />
      {/* Canopy over the storefront */}
      <path d="M20 62H140" strokeWidth="1.1" />
      {/* Storefront bay mullions */}
      <path d="M44 62V92M68 62V92M92 62V92M116 62V92" strokeWidth="0.7" />
      {/* Recessed entry */}
      <path d="M74 92V70H90V92" strokeWidth="1.4" />
      <Ground />
    </>
  );
}

function Industrial() {
  return (
    <>
      {/* Shallow-pitched roof with eaves */}
      <path d="M10 48L34 36H126L150 48" strokeWidth="1.4" />
      <path d="M10 48H150" strokeWidth="1.4" />
      <path d="M14 48V92M146 48V92" strokeWidth="1.4" />
      {/* Roof monitor / clerestory */}
      <path d="M60 36V28H100V36" strokeWidth="1" />
      {/* Overhead loading doors, with panel divisions */}
      <path d="M30 92V66H52V92M66 92V66H88V92M102 92V66H124V92" strokeWidth="1" />
      <path d="M30 74H52M30 83H52M66 74H88M66 83H88M102 74H124M102 83H124" strokeWidth="0.5" />
      {/* Man door */}
      <path d="M132 92V76H141V92" strokeWidth="1" />
      <Ground />
    </>
  );
}

function Land() {
  return (
    <>
      {/* North arrow */}
      <path d="M12 5L16.5 18L12 15L7.5 18Z" strokeWidth="0.8" fill="currentColor" />
      {/* Parcel boundary — dashed, the way a lot line is drawn */}
      <path d="M24 80L36 26L126 20L140 76Z" strokeWidth="1.2" strokeDasharray="6 3.5" />
      {/* Corner monuments */}
      <circle cx="24" cy="80" r="1.8" fill="currentColor" strokeWidth="0" />
      <circle cx="36" cy="26" r="1.8" fill="currentColor" strokeWidth="0" />
      <circle cx="126" cy="20" r="1.8" fill="currentColor" strokeWidth="0" />
      <circle cx="140" cy="76" r="1.8" fill="currentColor" strokeWidth="0" />
      {/* Grade contours */}
      <path d="M40 60C60 52 92 64 118 54" strokeWidth="0.6" />
      <path d="M44 71C66 65 96 75 126 66" strokeWidth="0.6" />
      {/* Frontage dimension */}
      <path d="M24 88V96M140 88V96" strokeWidth="0.7" />
      <path d="M24 92H140" strokeWidth="0.7" />
    </>
  );
}

const GLYPHS: Record<GlyphType, () => ReactElement> = {
  office: Office,
  retail: Retail,
  industrial: Industrial,
  land: Land,
};

export default function BuildingGlyph({ type, className = '' }: Props) {
  const Shape = GLYPHS[type];
  return (
    <svg
      viewBox="0 0 160 100"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
    >
      <Shape />
    </svg>
  );
}
